import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, RefreshCcw } from "lucide-react";
import { ClipLoader } from "react-spinners";
import { motion, AnimatePresence } from "framer-motion";
import "./Gallery.css";

const Gallery = () => {
    const [localImages, setLocalImages] = useState([]);
    const [apiImages, setApiImages] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const [imagesPerPage, setImagesPerPage] = useState(6);
    const [loadingApiImages, setLoadingApiImages] = useState(true);
    const [loadingImages, setLoadingImages] = useState([]);
    const [chevronSize, setChevronSize] = useState(24);
    const [zoom, setZoom] = useState(1);
    const [isError, setIsError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [failedImages, setFailedImages] = useState(new Set());

    // Constants
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 3000;

    // Load local images immediately
    useEffect(() => {
        const images = Array.from(
            { length: 108 },
            (_, i) => ({
                url: `/foto/img_${i + 1}.png`,
                name: `Local Image ${i + 1}`,
                isLocal: true
            })
        );
        
        setLoadingImages(Array(images.length).fill(true));
        setLocalImages(images);
    }, []);

    // Fungsi delay untuk retry
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // Fungsi retry untuk fetch data
    const fetchWithRetry = useCallback(async (url, options = {}, retryCount = 0) => {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);

            const response = await fetch(url, {
                ...options,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            if (retryCount < MAX_RETRIES) {
                await delay(RETRY_DELAY * Math.pow(2, retryCount));
                return fetchWithRetry(url, options, retryCount + 1);
            }
            throw error;
        }
    }, []);

    // Fetch gambar dari API
    const fetchApiImages = useCallback(async () => {
        try {
            setLoadingApiImages(true);
            setIsError(false);

            const data = await fetchWithRetry(
                "https://belajar-api-production.up.railway.app/images",
                {
                    headers: {
                        'Accept': 'application/json'
                    }
                }
            );

            const formattedApiImages = data
                .filter((img) => img?.url && typeof img.url === 'string')
                .map((img) => ({
                    url: img.url,
                    name: img.name || 'Untitled',
                    isLocal: false
                }));

            setApiImages(formattedApiImages);
            setLoadingImages(prev => [...prev, ...Array(formattedApiImages.length).fill(true)]);
            setRetryCount(0);
        } catch (error) {
            console.error("Failed to fetch API images:", error);
            setIsError(true);
            setRetryCount(prev => prev + 1);
            
            if (retryCount < MAX_RETRIES) {
                setTimeout(fetchApiImages, RETRY_DELAY * Math.pow(2, retryCount));
            }
        } finally {
            setLoadingApiImages(false);
        }
    }, [fetchWithRetry, retryCount]);

    // Initial fetch
    useEffect(() => {
        fetchApiImages();
    }, [fetchApiImages]);

    // Handle responsive layout
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 768) {
                setImagesPerPage(10);
                setChevronSize(24);
            } else {
                setImagesPerPage(9);
                setChevronSize(46);
            }
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Combine local and API images
    const allImages = [...apiImages, ...localImages];

    // Pagination calculation
    const indexOfLastImage = currentPage * imagesPerPage;
    const indexOfFirstImage = indexOfLastImage - imagesPerPage;
    const currentImages = allImages.slice(indexOfFirstImage, indexOfLastImage);
    const totalPages = Math.ceil(allImages.length / imagesPerPage);

    // Navigation handlers
    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Lightbox handlers
    const openLightbox = (index) => {
        setLightboxIndex(index);
        setZoom(1);
    };

    const closeLightbox = () => {
        setLightboxIndex(null);
        setZoom(1);
    };
    
    // Handle image load
    const handleImageLoad = (index) => {
        setLoadingImages(prevLoadingImages => {
            const updatedLoadingImages = [...prevLoadingImages];
            updatedLoadingImages[index] = false;
            return updatedLoadingImages;
        });
    };

    // Handle image error
    const handleImageError = async (index, image) => {
        console.error(`Failed to load image: ${image.url}`);
        
        setFailedImages(prev => {
            const updated = new Set(prev);
            updated.add(index);
            return updated;
        });

        setLoadingImages(prevLoadingImages => {
            const updatedLoadingImages = [...prevLoadingImages];
            updatedLoadingImages[index] = false;
            return updatedLoadingImages;
        });

        if (!image.isLocal && !failedImages.has(index)) {
            await delay(2000);
            setLoadingImages(prevLoadingImages => {
                const updatedLoadingImages = [...prevLoadingImages];
                updatedLoadingImages[index] = true;
                return updatedLoadingImages;
            });
            const img = new Image();
            img.src = image.url;
            img.onload = () => handleImageLoad(index);
            img.onerror = () => handleImageError(index, image);
        }
    };

    return (
        <section className="gallery">
            <div className="blur-background blur-top-left"></div>
            <h2>Gallery</h2>
            
            <div className="status-container">
                {loadingApiImages && !isError && (
                    <div className="api-status">
                        <div className="loading-dot-container">
                            <div className="loading-dot"></div>
                            <div className="loading-dot"></div>
                            <div className="loading-dot"></div>
                        </div>
                    </div>
                )}
                
                {isError && (
                    <div className="error-message">
                        <p>Gagal memuat beberapa gambar dari API.</p>
                        <button 
                            className="retry-button"
                            onClick={() => {
                                setRetryCount(0);
                                fetchApiImages();
                            }}
                        >
                            <RefreshCcw size={16} />
                            Coba Lagi
                        </button>
                    </div>
                )}
            </div>

            <motion.div className="gallery-grid">
                {currentImages.map((image, index) => (
                    <motion.div
                        key={`${image.url}-${index}`}
                        className="image-wrapper"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        {loadingImages[indexOfFirstImage + index] && (
                            <div className={`loading-placeholder ${image.isLocal ? 'local' : 'api'}`}>
                                <ClipLoader 
                                    color="#ffffff" 
                                    loading={true} 
                                    size={image.isLocal ? 50 : 50} 
                                />
                            </div>
                        )}
                        <img
                            src={image.url}
                            alt={image.name}
                            onClick={() => openLightbox(indexOfFirstImage + index)}
                            onLoad={() => handleImageLoad(indexOfFirstImage + index)}
                            onError={() => handleImageError(indexOfFirstImage + index, image)}
                            style={{
                                display: loadingImages[indexOfFirstImage + index] ? "none" : "block"
                            }}
                            className={failedImages.has(indexOfFirstImage + index) ? "failed-image" : ""}
                        />
                        {failedImages.has(indexOfFirstImage + index) && !image.isLocal && (
                            <div className="error-overlay">
                                <RefreshCcw 
                                    className="retry-icon" 
                                    size={24}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleImageError(indexOfFirstImage + index, image);
                                    }}
                                />
                            </div>
                        )}
                    </motion.div>
                ))}
            </motion.div>

            <div className="pagination">
                <button
                    className="nav-button prev"
                    onClick={prevPage}
                    disabled={currentPage === 1}
                >
                    <ChevronLeft size={chevronSize} />
                </button>

                <div className="page-dots">
                    {Array.from({ length: totalPages }, (_, page) => page + 1).map(
                        (pageNumber) => (
                            <span
                                key={pageNumber}
                                className={`dot ${
                                    currentPage === pageNumber ? "active" : ""
                                }`}
                                onClick={() => setCurrentPage(pageNumber)}
                            ></span>
                        )
                    )}
                </div>

                <button
                    className="nav-button next"
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                >
                    <ChevronRight size={chevronSize} />
                </button>
            </div>

            <AnimatePresence>
                {lightboxIndex !== null && (
                    <motion.div
                        className="lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <button className="close-button" onClick={closeLightbox}>
                            <X size={24} />
                        </button>
                        <motion.img
                            src={allImages[lightboxIndex].url}
                            alt={allImages[lightboxIndex].name}
                            style={{
                                transform: `scale(${zoom})`,
                                transition: "transform 0.3s ease-in-out",
                            }}
                        />
                        <button
                            className="lightbox-nav prev"
                            onClick={() =>
                                openLightbox(
                                    (lightboxIndex - 1 + allImages.length) % allImages.length
                                )
                            }
                        >
                            <ChevronLeft size={36} />
                        </button>
                        <button
                            className="lightbox-nav next"
                            onClick={() =>
                                openLightbox((lightboxIndex + 1) % allImages.length)
                            }
                        >
                            <ChevronRight size={36} />
                        </button>
                        <div className="zoom-controls">
                            <button onClick={() => setZoom((prev) => Math.min(prev + 0.1, 3))}>
                                <ZoomIn size={24} />
                            </button>
                            <button onClick={() => setZoom((prev) => Math.max(prev - 0.1, 0.5))}>
                                <ZoomOut size={24} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default Gallery;
